/* Stevenson Garage theme. Global behaviour: smooth scroll (desktop only), reveal on scroll,
   header compact and hide, drawers and dialogs, quantity inputs, details animation, search. */
(function () {
  'use strict';
  var ST = window.ST || {};
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var desktop = window.matchMedia('(min-width: 900px)').matches;

  /* ---------- Smooth scroll: Lenis, lerp 0.18, desktop only, never on phones ---------- */
  var lenis = null;
  if (ST.smoothScroll && !reduceMotion && desktop && finePointer && window.Lenis) {
    lenis = new window.Lenis({ lerp: 0.18, smoothWheel: true });
    (function raf(time) { lenis.raf(time); requestAnimationFrame(raf); })(0);
    document.documentElement.classList.add('has-lenis');
  }
  window.ST.lenis = lenis;
  window.ST.scrollTo = function (target, offset) {
    var el = typeof target === 'string' ? document.querySelector(target) : target;
    if (!el) return;
    var top = el.getBoundingClientRect().top + window.scrollY + (offset || 0);
    if (lenis) lenis.scrollTo(top, { duration: 1 });
    else window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  /* ---------- Reveal on scroll: rises in once, with a safety net so nothing stays hidden ---------- */
  var reveals = document.querySelectorAll('[data-reveal]');
  if (reveals.length && ST.reveal && !reduceMotion && 'IntersectionObserver' in window) {
    document.documentElement.classList.add('reveal-ready');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 });
    reveals.forEach(function (el, i) {
      el.style.setProperty('--reveal-delay', (Math.min(i % 6, 5) * 60) + 'ms');
      io.observe(el);
    });
    setTimeout(function () { reveals.forEach(function (el) { var r = el.getBoundingClientRect(); if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('is-in'); }); }, 1200);
  } else {
    reveals.forEach(function (el) { el.classList.add('is-in'); });
  }

  /* ---------- Header: always sticky; compact after 80px; optionally hides scrolling down (section setting) ---------- */
  var header = document.querySelector('[data-header]');
  if (header) {
    var lastY = window.scrollY, ticking = false;
    var setHeights = function () {
      var ann = document.querySelector('[data-announcement]');
      document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
      document.documentElement.style.setProperty('--announcement-height', (ann ? ann.offsetHeight : 0) + 'px');
    };
    setHeights();
    window.addEventListener('resize', setHeights);
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var y = window.scrollY;
        header.classList.toggle('is-compact', y > 80);
        var menuOpen = document.body.classList.contains('menu-open');
        var hideOnScroll = header.dataset.hideOnScroll === 'true';
        header.classList.toggle('is-hidden', hideOnScroll && !menuOpen && y > lastY && y > 240);
        lastY = y;
        ticking = false;
      });
    }, { passive: true });
  }

  /* ---------- Generic toggles: [data-toggle="selector"] opens a drawer, dialog or panel ---------- */
  var lastFocus = null;
  function lockScroll(lock) {
    document.body.classList.toggle('scroll-lock', lock);
    if (lenis) { lock ? lenis.stop() : lenis.start(); }
  }
  function openPanel(panel) {
    if (!panel || panel.classList.contains('is-open')) return;
    lastFocus = document.activeElement;
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(function () { panel.classList.add('is-open'); });
    if (panel.hasAttribute('data-lock')) lockScroll(true);
    document.body.classList.add(panel.dataset.bodyClass || 'panel-open');
    var first = panel.querySelector('[autofocus], input:not([type=hidden]), a[href], button:not([data-close])');
    if (first) setTimeout(function () { first.focus({ preventScroll: true }); }, 120);
    panel.dispatchEvent(new CustomEvent('st:open'));
  }
  function closePanel(panel) {
    if (!panel || !panel.classList.contains('is-open')) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    if (panel.hasAttribute('data-lock')) lockScroll(false);
    document.body.classList.remove(panel.dataset.bodyClass || 'panel-open');
    setTimeout(function () { if (!panel.classList.contains('is-open')) panel.hidden = true; }, reduceMotion ? 0 : 420);
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    panel.dispatchEvent(new CustomEvent('st:close'));
  }
  window.ST.openPanel = openPanel;
  window.ST.closePanel = closePanel;

  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-toggle]');
    if (t) {
      e.preventDefault();
      var panel = document.querySelector(t.getAttribute('data-toggle'));
      if (!panel) return;
      panel.classList.contains('is-open') ? closePanel(panel) : openPanel(panel);
      t.setAttribute('aria-expanded', panel.classList.contains('is-open'));
      return;
    }
    var c = e.target.closest('[data-close]');
    if (c) { e.preventDefault(); closePanel(c.closest('[data-panel]')); return; }
    var scrim = e.target.closest('[data-panel]');
    if (scrim && e.target === scrim && scrim.hasAttribute('data-close-on-scrim')) closePanel(scrim);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var open = document.querySelector('[data-panel].is-open');
    if (open) closePanel(open);
  });
  /* focus trap inside open panels */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var open = document.querySelector('[data-panel].is-open[data-lock]');
    if (!open) return;
    var items = open.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* ---------- Quantity inputs ---------- */
  document.addEventListener('click', function (e) {
    var b = e.target.closest('[data-qty]');
    if (!b) return;
    e.preventDefault();
    var wrap = b.closest('[data-qty-wrap]');
    var input = wrap && wrap.querySelector('input[name="quantity"], input[data-qty-input]');
    if (!input) return;
    var step = parseInt(input.step || '1', 10) || 1;
    var min = parseInt(input.min || '1', 10) || 1;
    var max = input.max ? parseInt(input.max, 10) : Infinity;
    var v = parseInt(input.value || '1', 10) || min;
    v = b.dataset.qty === 'plus' ? v + step : v - step;
    v = Math.max(min, Math.min(max, v));
    input.value = v;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Animated <details> (accordion height) ---------- */
  document.querySelectorAll('details[data-animate]').forEach(function (d) {
    var summary = d.querySelector('summary'), content = d.querySelector('[data-details-content]');
    if (!summary || !content) return;
    summary.addEventListener('click', function (e) {
      if (reduceMotion) return;
      e.preventDefault();
      if (d.open) {
        var h = content.offsetHeight;
        content.style.height = h + 'px';
        requestAnimationFrame(function () { content.style.height = '0px'; });
        content.addEventListener('transitionend', function done() { d.open = false; content.style.height = ''; content.removeEventListener('transitionend', done); });
      } else {
        d.open = true;
        var full = content.scrollHeight;
        content.style.height = '0px';
        requestAnimationFrame(function () { content.style.height = full + 'px'; });
        content.addEventListener('transitionend', function done() { content.style.height = ''; content.removeEventListener('transitionend', done); });
      }
    });
  });

  /* ---------- Localization and sort forms submit on change ---------- */
  document.querySelectorAll('[data-submit-on-change]').forEach(function (form) {
    form.addEventListener('change', function () { form.submit(); });
  });

  /* ---------- Predictive search (lightweight) ---------- */
  var searchInput = document.querySelector('[data-predictive-input]');
  var searchResults = document.querySelector('[data-predictive-results]');
  if (searchInput && searchResults && ST.routes && ST.routes.predictive) {
    var timer = null, controller = null;
    searchInput.addEventListener('input', function () {
      clearTimeout(timer);
      var q = searchInput.value.trim();
      if (q.length < 2) { searchResults.innerHTML = ''; searchResults.hidden = true; return; }
      timer = setTimeout(function () {
        if (controller) controller.abort();
        controller = new AbortController();
        fetch(ST.routes.predictive + '?q=' + encodeURIComponent(q) + '&resources[type]=product,collection&resources[limit]=6&section_id=predictive-search', { signal: controller.signal })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var inner = doc.querySelector('#shopify-section-predictive-search');
            searchResults.innerHTML = inner ? inner.innerHTML : '';
            searchResults.hidden = !searchResults.innerHTML.trim();
          }).catch(function () {});
      }, 220);
    });
  }

  /* ---------- Share buttons ---------- */
  document.addEventListener('click', function (e) {
    var s = e.target.closest('[data-share]');
    if (!s) return;
    e.preventDefault();
    var url = s.dataset.share || location.href;
    if (navigator.share) { navigator.share({ url: url, title: document.title }).catch(function () {}); return; }
    navigator.clipboard && navigator.clipboard.writeText(url).then(function () {
      var label = s.querySelector('[data-share-label]');
      if (!label) return;
      var old = label.textContent; label.textContent = s.dataset.copied || 'Link copied';
      setTimeout(function () { label.textContent = old; }, 1600);
    });
  });

  /* ---------- Country switch. Shopify only sends visitors to their own market by itself on the Plus plan,
     so on first visit we ask Shopify which market fits this visitor and move them there once.
     A pick in the footer country selector wins for good, so Jeff can browse the US side from Canada. ---------- */
  (function () {
    if (!window.Shopify || !Shopify.country || !window.fetch) return;
    var KEY = 'st_country_set';
    try { if (localStorage.getItem(KEY)) return; } catch (e) {}
    document.addEventListener('submit', function (e) {
      if (e.target && e.target.querySelector && e.target.querySelector('[name="country_code"]')) { try { localStorage.setItem(KEY, 'manual'); } catch (err) {} }
    }, true);
    var root = (Shopify.routes && Shopify.routes.root) || '/';
    fetch(root + 'browsing_context_suggestions.json?country[enabled]=true&country[exclude]=' + encodeURIComponent(Shopify.country))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var s = data && data.suggestions && data.suggestions[0];
        var code = s && s.parts && s.parts.country && s.parts.country.handle;
        if (!code || code === Shopify.country) return;
        try { localStorage.setItem(KEY, code); } catch (e) {}
        var f = document.createElement('form'); f.method = 'post'; f.action = root + 'localization'; f.hidden = true;
        [['_method', 'put'], ['country_code', code], ['return_to', location.pathname + location.search]].forEach(function (p) {
          var i = document.createElement('input'); i.type = 'hidden'; i.name = p[0]; i.value = p[1]; f.appendChild(i);
        });
        document.body.appendChild(f); f.submit();
      }).catch(function () {});
  })();

  /* ---------- Shopify theme editor: re-run reveals when sections load ---------- */
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-reveal]').forEach(function (el) { el.classList.add('is-in'); });
  });
})();
